import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';

function priorityClass(priorite) {
  return `priority-badge p${priorite || 0}`;
}

function MotsCles() {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [motscles, setMotscles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [volumeFilter, setVolumeFilter] = useState('all');
  const [difficulteFilter, setDifficulteFilter] = useState('all');
  const [intentionFilter, setIntentionFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');
  const [error, setError] = useState('');

  const loadSites = async () => {
    const response = await apiFetch('/api/sites');
    if (!response.ok) throw new Error('Erreur chargement sites');
    const data = await response.json();
    const nextSites = Array.isArray(data) ? data : [];
    setSites(nextSites);
    if (nextSites.length > 0) {
      setSelectedSiteId((current) => current || nextSites[0].id);
    }
  };

  const loadKeywords = async (siteId) => {
    if (!siteId) {
      setMotscles([]);
      return;
    }
    const response = await apiFetch(`/api/motscles/${siteId}`);
    if (!response.ok) throw new Error('Erreur chargement mots-clés');
    const data = await response.json();
    setMotscles(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        setError('');
        await loadSites();
      } catch (requestError) {
        setError('Impossible de charger les sites.');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError('');
        await loadKeywords(selectedSiteId);
      } catch (requestError) {
        setError('Impossible de charger les mots-clés.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [selectedSiteId]);

  const handleAnalyze = async () => {
    if (!selectedSiteId) return;
    try {
      setError('');
      setAnalysing(true);
      const response = await apiFetch(`/api/motscles/rechercher/${selectedSiteId}`, { method: 'POST' });
      if (!response.ok) throw new Error('Erreur recherche mots-clés');
      await loadKeywords(selectedSiteId);
    } catch (requestError) {
      setError('Impossible de lancer la recherche de mots-clés.');
    } finally {
      setAnalysing(false);
    }
  };

  const handleClearAllKeywords = async () => {
    if (!selectedSiteId) return;
    try {
      setError('');
      const response = await apiFetch(`/api/motscles/vider/${selectedSiteId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Erreur vidage mots-clés');
      setMotscles([]);
    } catch (requestError) {
      setError('Impossible de vider les mots-clés de ce site.');
    }
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      const response = await apiFetch(`/api/motscles/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Erreur suppression');
      setMotscles((current) => current.filter((item) => item.id !== id));
    } catch (requestError) {
      setError('Impossible de supprimer ce mot-clé.');
    }
  };

  const handleToggleStatut = async (item) => {
    const nextStatut = item.statut === 'actif' ? 'archivé' : 'actif';
    try {
      setError('');
      const response = await apiFetch(`/api/motscles/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: nextStatut }),
      });
      if (!response.ok) throw new Error('Erreur update statut');
      const updated = await response.json();
      setMotscles((current) => current.map((row) => (row.id === item.id ? updated : row)));
    } catch (requestError) {
      setError('Impossible de modifier le statut.');
    }
  };

  const filteredRows = useMemo(() => {
    const rows = motscles.filter((row) => {
      if (volumeFilter !== 'all' && row.volume !== volumeFilter) return false;
      if (difficulteFilter !== 'all' && row.difficulte !== difficulteFilter) return false;
      if (intentionFilter !== 'all' && row.intention !== intentionFilter) return false;
      return true;
    });

    return rows.sort((a, b) => {
      const pa = Number.isFinite(a.priorite) ? a.priorite : 99;
      const pb = Number.isFinite(b.priorite) ? b.priorite : 99;
      return sortOrder === 'asc' ? pa - pb : pb - pa;
    });
  }, [motscles, volumeFilter, difficulteFilter, intentionFilter, sortOrder]);

  return (
    <section className="motscles-page">
      <header className="motscles-header">
        <div>
          <h1>Recherche de mots-clés</h1>
          <p>Identifiez les meilleures opportunités SEO pour chaque site.</p>
        </div>
        <div className="motscles-header-actions">
          <button
            type="button"
            className="primary-button motscles-analyze-button"
            onClick={handleAnalyze}
            disabled={!selectedSiteId || analysing}
          >
            {analysing ? 'Analyse en cours...' : 'Analyser les mots-clés'}
          </button>
          <button
            type="button"
            className="site-delete-button motscles-clear-button"
            onClick={handleClearAllKeywords}
            disabled={!selectedSiteId || loading}
          >
            Vider tous les mots-clés
          </button>
        </div>
      </header>

      {error && <p className="sites-error">{error}</p>}

      <div className="motscles-controls">
        <div className="motscles-control">
          <label htmlFor="motsclesSiteSelect">Site</label>
          <select
            id="motsclesSiteSelect"
            value={selectedSiteId}
            onChange={(event) => setSelectedSiteId(event.target.value)}
            disabled={loading || sites.length === 0}
          >
            {sites.length === 0 ? (
              <option value="">Aucun site</option>
            ) : (
              sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.nom}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="motscles-control">
          <label htmlFor="volumeFilter">Volume</label>
          <select id="volumeFilter" value={volumeFilter} onChange={(event) => setVolumeFilter(event.target.value)}>
            <option value="all">Tous</option>
            <option value="faible">Faible</option>
            <option value="moyen">Moyen</option>
            <option value="élevé">Élevé</option>
          </select>
        </div>

        <div className="motscles-control">
          <label htmlFor="difficulteFilter">Difficulté</label>
          <select
            id="difficulteFilter"
            value={difficulteFilter}
            onChange={(event) => setDifficulteFilter(event.target.value)}
          >
            <option value="all">Toutes</option>
            <option value="faible">Faible</option>
            <option value="moyen">Moyen</option>
            <option value="élevé">Élevé</option>
          </select>
        </div>

        <div className="motscles-control">
          <label htmlFor="intentionFilter">Intention</label>
          <select
            id="intentionFilter"
            value={intentionFilter}
            onChange={(event) => setIntentionFilter(event.target.value)}
          >
            <option value="all">Toutes</option>
            <option value="informationnelle">Informationnelle</option>
            <option value="transactionnelle">Transactionnelle</option>
            <option value="navigationnelle">Navigationnelle</option>
          </select>
        </div>

        <div className="motscles-control">
          <label htmlFor="sortPriorite">Tri priorité</label>
          <select id="sortPriorite" value={sortOrder} onChange={(event) => setSortOrder(event.target.value)}>
            <option value="asc">1 → 5</option>
            <option value="desc">5 → 1</option>
          </select>
        </div>
      </div>

      <div className="motscles-table-wrapper">
        <table className="motscles-table">
          <thead>
            <tr>
              <th>Mot-clé</th>
              <th>Volume</th>
              <th>Difficulté</th>
              <th>Intention</th>
              <th>Priorité</th>
              <th>Statut utilisé</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">Chargement...</td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan="7">Aucun mot-clé pour ces filtres.</td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.motcle}</td>
                  <td>{row.volume || '-'}</td>
                  <td>{row.difficulte || '-'}</td>
                  <td>{row.intention || '-'}</td>
                  <td>
                    <span className={priorityClass(row.priorite)}>{row.priorite || '-'}</span>
                  </td>
                  <td>
                    <span className={`keyword-status ${row.statut === 'actif' ? 'active' : 'archived'}`}>
                      {row.statut} / {row.utilise ? 'utilisé' : 'non utilisé'}
                    </span>
                  </td>
                  <td>
                    <div className="motscles-actions">
                      <button type="button" className="site-edit-button" onClick={() => handleToggleStatut(row)}>
                        {row.statut === 'actif' ? 'Archiver' : 'Activer'}
                      </button>
                      <button type="button" className="site-delete-button" onClick={() => handleDelete(row.id)}>
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default MotsCles;
